import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faCalendar, faTicketAlt, faCreditCard, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './AuditDetailModal.css';

const AuditDetailModal = ({ audit, onClose, onNavigate }) => {

  const formatActionTime = (actionTimeArray) => {
    if (!actionTimeArray || !Array.isArray(actionTimeArray) || actionTimeArray.length < 7) {
      return 'N/A';
    }

    const [year, month, day, hour, minute, second] = actionTimeArray;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return date.toLocaleString();
  };

  const getActionTypeIcon = (actionType) => {
    const icons = {
      'PURCHASE': '🛒',
      'VALIDATE': '✅',
      'VALIDATE_QR': '📱',
      'FIRST_YEAR_VALIDATE': '🆕',
      'VALIDATE_GENERIC_QR': '🎫',
      'TRANSFER_FROM': '📤',
      'TRANSFER_TO': '📥',
      'USER_TRANSFER_FROM': '👤📤',
      'USER_TRANSFER_TO': '👤📥',
      'BULK_CREATE': '📦',
      'BULK_UPDATE': '🔄',
      'CANCELLATION': '❌',
      'REFUND': '💰',
      'UPDATE': '✏️',
      'DELETE': '🗑️'
    };
    return icons[actionType] || '📋';
  };

  const getActionTypeColor = (actionType) => {
    const colors = {
      'PURCHASE': '#4CAF50',
      'VALIDATE': '#2196F3',
      'VALIDATE_QR': '#2196F3',
      'FIRST_YEAR_VALIDATE': '#2196F3',
      'VALIDATE_GENERIC_QR': '#2196F3',
      'TRANSFER_FROM': '#FF9800',
      'TRANSFER_TO': '#FF9800',
      'USER_TRANSFER_FROM': '#FF9800',
      'USER_TRANSFER_TO': '#FF9800',
      'BULK_CREATE': '#9C27B0',
      'BULK_UPDATE': '#9C27B0',
      'CANCELLATION': '#F44336',
      'REFUND': '#4CAF50',
      'UPDATE': '#2196F3',
      'DELETE': '#D32F2F'
    };
    return colors[actionType] || '#757575';
  };

  const formatEventDate = (dateStr, timezone) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      if (timezone) {
        return date.toLocaleString('en-US', {
          timeZone: timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
          timeZoneName: 'short'
        });
      }
      return date.toLocaleString();
    } catch (e) {
      return dateStr;
    }
  };

  if (!audit) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content audit-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <span 
              className="action-type-badge"
              style={{ backgroundColor: getActionTypeColor(audit.actionType) }}
            >
              {getActionTypeIcon(audit.actionType)} {audit.actionType.replace(/_/g, ' ')}
            </span>
            <span className="audit-id">Audit #{audit.id}</span>
          </div>
          <button className="close-modal-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {/* User Information Section */}
          <div className="detail-section">
            <h3><FontAwesomeIcon icon={faUser} /> User Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">IBO Number:</span>
                <span className="detail-value clickable" onClick={() => onNavigate('iboNumber', audit.iboNumber)}>
                  {audit.iboNumber}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{audit.userName || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{audit.userEmail || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{audit.userPhone || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Team:</span>
                <span className="detail-value">{audit.teamName || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Upline Platinum:</span>
                <span className="detail-value">{audit.uplinePlatinumIbo || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Event Information Section */}
          <div className="detail-section">
            <h3><FontAwesomeIcon icon={faCalendar} /> Event Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Event ID:</span>
                <span className="detail-value">{audit.eventId || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Event Name:</span>
                <span className="detail-value clickable" onClick={() => onNavigate('eventName', audit.eventName)}>
                  {audit.eventName || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Event Type:</span>
                <span className="detail-value">{audit.eventType || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Event Start Date:</span>
                <span className="detail-value">{formatEventDate(audit.eventStartDate, audit.eventTimezone)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Event End Date:</span>
                <span className="detail-value">{formatEventDate(audit.eventEndDate, audit.eventTimezone)}</span>
              </div>
            </div>
          </div>

          {/* Ticket Information Section */}
          <div className="detail-section">
            <h3><FontAwesomeIcon icon={faTicketAlt} /> Ticket Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Ticket ID:</span>
                <span className="detail-value">{audit.ticketId || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Quantity:</span>
                <span className="detail-value">{audit.ticketQuantity || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Ticket Type:</span>
                <span className="detail-value">{audit.ticketType || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className="detail-value">{audit.ticketStatus || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Transaction Details Section */}
          <div className="detail-section">
            <h3><FontAwesomeIcon icon={faCreditCard} /> Transaction Details</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Order ID:</span>
                <span className="detail-value">{audit.orderId || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Payment ID:</span>
                <span className="detail-value">{audit.paymentId || 'N/A'}</span>
              </div>
              {(audit.paymentStatus || audit.amount) && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value">
                      {audit.amount ? `${audit.currency || 'USD'} ${audit.amount}` : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Status:</span>
                    <span className={`detail-value payment-status-badge ${audit.paymentStatus?.toLowerCase()}`}>
                      {audit.paymentStatus || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Method:</span>
                    <span className="detail-value">{audit.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Provider:</span>
                    <span className="detail-value">{audit.paymentProvider || 'N/A'}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action Details Section */}
          <div className="detail-section">
            <h3><FontAwesomeIcon icon={faInfoCircle} /> Action Details</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Action By:</span>
                <span className="detail-value">{audit.actionBy}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Action Time:</span>
                <span className="detail-value">{formatActionTime(audit.actionTime)}</span>
              </div>
              <div className="detail-item full-width">
                <span className="detail-label">Details:</span>
                <span className="detail-value">{audit.details}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailModal;

